import { isDefined } from "@clipboard-health/util-ts";
import { GroupChannelHandler } from "@sendbird/chat/groupChannel";
import { UserMessage } from "@sendbird/chat/message";
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line import/no-named-as-default
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

export interface UseDetectCredentialKeywordInChatOptions {
  workerId: string;
  workplaceId: string | undefined;
  enabled?: boolean;
}

const CREDENTIALS_KEYWORDS = ["user", "pass", "pw", "login", "log in", "creds", "credentials"];

const POLLING_DURATION_MS = 30_000;
const CHECK_RECENT_MESSAGES_COUNT = 5;

/**
 * Monitors chat messages for charting credential keywords.
 *
 * Checks recent messages on mount and monitors new messages in real-time.
 */
export function useDetectCredentialKeywordInChat(options: UseDetectCredentialKeywordInChatOptions) {
  const { workerId, workplaceId, enabled = true } = options;

  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  const [hasRecentlyDetectedCredentialsKeyword, setHasRecentlyDetectedCredentialsKeyword] =
    useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (hasRecentlyDetectedCredentialsKeyword) {
      timeoutRef.current = setTimeout(() => {
        setHasRecentlyDetectedCredentialsKeyword(false);
      }, POLLING_DURATION_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hasRecentlyDetectedCredentialsKeyword]);

  function messageContainsKeywords(message: string): boolean {
    const lowerCaseMessage = message.toLowerCase();
    return CREDENTIALS_KEYWORDS.some((credentialKeyword) =>
      lowerCaseMessage.includes(credentialKeyword.toLowerCase())
    );
  }

  /**
   * Check recent messages on mount for keywords
   */
  useEffect(() => {
    if (!enabled || !sdk?.groupChannel || !isDefined(workerId) || !isDefined(workplaceId)) {
      return;
    }

    async function checkRecentMessages() {
      try {
        const channelUrl = `${workplaceId}_${workerId}`;
        const channel = await sdk.groupChannel.getChannel(channelUrl);

        const messageListQuery = channel.createPreviousMessageListQuery({
          limit: CHECK_RECENT_MESSAGES_COUNT,
          reverse: false,
        });

        const messages = await messageListQuery.load();

        const hasKeywordsInRecentMessages = messages.some((message) => {
          if (message instanceof UserMessage) {
            const messageText = message.message || "";

            return messageContainsKeywords(messageText);
          }

          return false;
        });

        if (hasKeywordsInRecentMessages) {
          setHasRecentlyDetectedCredentialsKeyword(true);
        }
      } catch {}
    }

    void checkRecentMessages();
  }, [sdk, workerId, workplaceId, enabled]);

  useEffect(() => {
    if (!enabled || !sdk?.groupChannel || !isDefined(workerId) || !isDefined(workplaceId)) {
      return undefined;
    }

    const handlerId = uuidv4();

    const channelHandler = new GroupChannelHandler({
      onMessageReceived: (_channel, message) => {
        if (message instanceof UserMessage) {
          const messageText = message.message || "";

          if (messageContainsKeywords(messageText)) {
            setHasRecentlyDetectedCredentialsKeyword(true);
          }
        }
      },
    });

    sdk.groupChannel.addGroupChannelHandler(handlerId, channelHandler);

    return () => {
      sdk.groupChannel.removeGroupChannelHandler(handlerId);
    };
  }, [sdk, workerId, workplaceId, enabled]);

  return {
    hasRecentlyDetectedCredentialsKeyword,
  };
}
